import { EditorState, Command } from "prosemirror-state";
import {
	addColumnAfter,
	addColumnBefore,
	addRowAfter,
	addRowBefore,
	deleteColumn,
	deleteRow,
	isInTable
} from "prosemirror-tables";
import { useCallback, useMemo } from "react";
import { insertTable } from "../commands/insertTable";
import { hasNodes } from "../schema";
import { CallbackWithSchemaResult, useExtendWithSchema } from "./useExtendWithSchema";
import { Schema } from "prosemirror-model";

export type TableToolHandlers = {
	areSupportedBySchema: true;
	insertTable(rows: number, columns: number): void;
	isInTable: boolean;
	addColumnBefore(): void;
	addColumnAfter(): void;
	addRowAbove(): void;
	addRowBelow(): void;
	removeColumn(): void;
	removeRow(): void;
} | {
	areSupportedBySchema: false;
};

export function useTableTools(
	editorState: EditorState,
	applyCommandSequence: (commands: readonly Command[]) => void,
	focus?: () => void
): TableToolHandlers {
	const insertTableHandler: CallbackWithSchemaResult<
		[rows: number, columns: number],
		void
	> = useExtendWithSchema(
		editorState.schema,
		schema => hasNodes(
			schema,
			[
				"table",
				"table_row",
				"table_cell",
				"paragraph"
			]
		),
		useCallback(
			(
				schema: Schema<
					| "table"
					| "table_row"
					| "table_cell"
					| "paragraph",
					never
				>,
				rows: number,
				columns: number
			) => {
				applyCommandSequence([
					insertTable(schema ,rows, columns)
				]);

				focus?.();
			},
			[applyCommandSequence, focus]
		)
	);



	return useMemo(
		(): TableToolHandlers => {
			return insertTableHandler.isSupportedBySchema
				? {
					areSupportedBySchema: true,
					insertTable: insertTableHandler,
					addColumnAfter(): void {
						applyCommandSequence(
							[addColumnAfter]
						);

						focus?.();
					},
					addColumnBefore(): void {
						applyCommandSequence(
							[addColumnBefore]
						);

						focus?.();
					},
					addRowAbove(): void {
						applyCommandSequence(
							[addRowBefore]
						);

						focus?.();
					},
					addRowBelow(): void {
						applyCommandSequence(
							[addRowAfter]
						);

						focus?.();
					},
					removeColumn(): void {
						applyCommandSequence(
							[deleteColumn]
						);

						focus?.();
					},
					removeRow(): void {
						applyCommandSequence(
							[deleteRow]
						);

						focus?.();
					},
					isInTable: isInTable(editorState)
				}
			: {
				areSupportedBySchema: false
			};
		},
		[
			applyCommandSequence,
			editorState,
			focus,
			insertTableHandler
		]
	);
};